import { useEffect } from "react";
import { Link } from "@/components/LangLink";
import { Phone, Smartphone, Mail, MapPin, Globe, Download, ArrowUpRight } from "lucide-react";
import { CONTACT, NAV } from "@/lib/site";
import { openCookiePreferences, loadIubendaLegal, startConsentWatcher } from "@/lib/iubenda";
import { CertificationsFooterLink } from "@/components/Certifications";

const Footer = () => {
  useEffect(() => {
    loadIubendaLegal();
    startConsentWatcher();
  }, []);
  return (
    <footer className="bg-navy text-white" data-testid="main-footer">
      <div className="container-x py-16 lg:py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        {/* Brand */}
        <div>
          <img src="/brands/medisistem_white.png" alt="Medisistem" className="h-9 w-auto" />
          <p className="mt-4 font-serif italic text-green text-sm">{CONTACT.payoff}</p>
          <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-xs">
            Centro specializzato nella terapia elasto-compressiva. {CONTACT.since}.
          </p>
        </div>

        {/* Quick links */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50 mb-5">Navigazione</h4>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm text-white/75 hover:text-green transition-colors duration-200"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Brands */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50 mb-5">Brand</h4>
          <ul className="space-y-3">
            <li><Link to="/prodotti/lumaven" data-testid="footer-brand-lumaven" className="text-sm text-white/75 hover:text-green transition-colors">LUMAVEN</Link></li>
            <li><Link to="/prodotti/schiebler" data-testid="footer-brand-schiebler" className="text-sm text-white/75 hover:text-green transition-colors">SCHIEBLER</Link></li>
          </ul>
          <div className="mt-7 space-y-3">
            <CertificationsFooterLink className="flex items-center gap-2 text-sm text-white/75 hover:text-green transition-colors" />
            <a
              href="/downloads/medisistem-company-profile.pdf"
              download
              data-testid="download-company-profile"
              className="flex items-center gap-2 text-sm text-white/75 hover:text-green transition-colors"
            >
              <Download size={15} /> Scarica Company Profile
            </a>
            <a
              href="/downloads/medisistem-catalogo.pdf"
              download
              data-testid="download-catalogo"
              className="flex items-center gap-2 text-sm text-white/75 hover:text-green transition-colors"
            >
              <Download size={15} /> Scarica Catalogo
            </a>
          </div>
        </div>

        {/* Contacts */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50 mb-5">Contatti</h4>
          <ul className="space-y-3.5 text-sm text-white/75">
            <li className="flex items-start gap-3"><MapPin size={16} className="mt-0.5 text-green shrink-0" /> <a href={CONTACT.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-maps-link" className="transition-colors hover:text-white">{CONTACT.address}</a></li>
            <li className="flex items-center gap-3"><Phone size={16} className="text-green shrink-0" /> {CONTACT.tel1} · {CONTACT.tel2}</li>
            <li className="flex items-center gap-3"><Smartphone size={16} className="text-green shrink-0" /> {CONTACT.mobile}</li>
            <li className="flex items-center gap-3"><Mail size={16} className="text-green shrink-0" /> {CONTACT.email}</li>
            <li className="flex items-center gap-3"><Globe size={16} className="text-green shrink-0" /> {CONTACT.web}</li>
          </ul>
          <Link to="/il-metodo#prenota" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-green hover:gap-2.5 transition-all">
            Prenota una misurazione <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col gap-4">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs" data-testid="footer-legal-links">
            {/* Link legali iubenda — snippet ufficiali forniti da iubenda, classi e attributi invariati */}
            <a href="https://www.iubenda.com/privacy-policy/10902254" className="iubenda-white iubenda-noiframe iubenda-embed text-white/60 hover:text-green transition-colors" title="Privacy Policy " data-testid="footer-privacy">Privacy Policy</a>
            <a href="https://www.iubenda.com/privacy-policy/10902254/cookie-policy" className="iubenda-white iubenda-noiframe iubenda-embed text-white/60 hover:text-green transition-colors" title="Cookie Policy " data-testid="footer-cookie">Cookie Policy</a>
            <Link to="/termini-e-condizioni" data-testid="footer-termini" className="text-white/60 hover:text-green transition-colors">Condizioni d&apos;uso</Link>
            <button type="button" onClick={openCookiePreferences} data-testid="footer-manage-cookies" className="text-white/60 hover:text-green transition-colors">Gestisci preferenze cookie</button>
          </nav>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-x-4 gap-y-1 text-xs text-white/45 text-center">
            <p>© {new Date().getFullYear()} {CONTACT.legalName}. Tutti i diritti riservati.</p>
            <span className="hidden sm:inline text-white/25">·</span>
            <p>P. IVA {CONTACT.piva}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
